import type { Auth, User } from 'firebase/auth';

import configuration from '~/configuration';
import { ApiErrorCode } from '~/core/errors/codes';
import {
  RECOVERY_HEADER_VALUE,
  SESSION_RECOVERY_QUERY_PARAM,
  SESSION_RECOVERY_REQUIRED_HEADER,
} from '~/core/session/session-recovery-constants';

const RETURN_URL_QUERY_PARAM = 'returnUrl';
const EXPLICIT_SIGN_OUT_QUERY_PARAM = 'signOut';
const RECOVERABLE_RESPONSE_STATUSES = new Set([401, 403]);
const AUTH_HYDRATION_WAIT_MS = 2_000;

type SessionRecoveryAuth = Pick<Auth, 'currentUser' | 'signOut'> &
  Partial<Pick<Auth, 'onAuthStateChanged'>>;

let hasStartedRecoveryNavigation = false;
let suppressNextNullAuthEvent = false;

export function isTerminalSessionErrorPayload(payload: unknown): boolean {
  return (
    hasApiErrorCode(payload, ApiErrorCode.AUTH_SESSION_EXPIRED) ||
    hasApiErrorCode(payload, ApiErrorCode.AUTH_SESSION_INVALID)
  );
}

export function isMissingSessionErrorPayload(payload: unknown): boolean {
  return hasApiErrorCode(payload, ApiErrorCode.AUTH_SESSION_MISSING);
}

export async function readApiErrorPayload(
  response: Response,
): Promise<unknown> {
  try {
    return await response.clone().json();
  } catch {
    return null;
  }
}

export async function readTerminalSessionErrorResponse(
  response: Response,
): Promise<boolean> {
  if (!RECOVERABLE_RESPONSE_STATUSES.has(response.status)) {
    return false;
  }

  if (
    response.headers.get(SESSION_RECOVERY_REQUIRED_HEADER) ===
    RECOVERY_HEADER_VALUE
  ) {
    return true;
  }

  return isTerminalSessionErrorPayload(await readApiErrorPayload(response));
}

export async function readMissingSessionErrorResponse(
  response: Response,
): Promise<boolean> {
  if (!RECOVERABLE_RESPONSE_STATUSES.has(response.status)) {
    return false;
  }

  return isMissingSessionErrorPayload(await readApiErrorPayload(response));
}

export function buildSessionRecoveryUrl(returnUrl?: string): string {
  const params = new URLSearchParams({
    [RETURN_URL_QUERY_PARAM]: sanitizeClientSessionReturnUrl(
      returnUrl ?? getCurrentPath(),
    ),
    [SESSION_RECOVERY_QUERY_PARAM]: 'true',
  });

  return `${configuration.paths.signIn}?${params.toString()}`;
}

export function redirectToSignInForSessionRecovery(returnUrl?: string): void {
  replaceLocationOnce(buildSessionRecoveryUrl(returnUrl));
}

export function redirectToSignInForMissingSession(returnUrl?: string): void {
  const params = new URLSearchParams({
    [RETURN_URL_QUERY_PARAM]: sanitizeClientSessionReturnUrl(
      returnUrl ?? getCurrentPath(),
    ),
  });

  replaceLocationOnce(`${configuration.paths.signIn}?${params.toString()}`);
}

export async function signOutClientForSessionRecovery(
  auth: SessionRecoveryAuth,
): Promise<void> {
  const user = auth.currentUser ?? (await waitForHydratedAuthUser(auth));

  if (!user) {
    suppressNextNullAuthEvent = false;
    return;
  }

  suppressNextNullAuthEvent = true;

  try {
    await auth.signOut();
  } catch (error) {
    suppressNextNullAuthEvent = false;
    throw error;
  }
}

function waitForHydratedAuthUser(
  auth: Pick<Auth, 'currentUser'> & Partial<Pick<Auth, 'onAuthStateChanged'>>,
): Promise<User | null> {
  const onAuthStateChanged = auth.onAuthStateChanged?.bind(auth);

  if (!onAuthStateChanged) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    let settled = false;
    const cleanup: {
      timeout?: ReturnType<typeof setTimeout>;
      unsubscribe?: () => void;
    } = {};

    const settle = (user: User | null) => {
      if (settled) {
        return;
      }

      settled = true;

      if (cleanup.timeout) {
        clearTimeout(cleanup.timeout);
      }

      cleanup.unsubscribe?.();
      resolve(user ?? auth.currentUser);
    };

    cleanup.timeout = setTimeout(
      () => settle(auth.currentUser),
      AUTH_HYDRATION_WAIT_MS,
    );
    cleanup.unsubscribe = onAuthStateChanged((user) => settle(user));

    if (settled) {
      cleanup.unsubscribe();
    }
  });
}

export function consumeSessionRecoveryClientSignOutSuppression(): boolean {
  if (!suppressNextNullAuthEvent) {
    return false;
  }

  suppressNextNullAuthEvent = false;
  return true;
}

export function clearSessionRecoveryClientSignOutSuppression(): void {
  suppressNextNullAuthEvent = false;
}

export function resetSessionRecoveryForTests(): void {
  hasStartedRecoveryNavigation = false;
  suppressNextNullAuthEvent = false;
}

export function sanitizeClientSessionReturnUrl(
  value: unknown,
  defaultPath = configuration.paths.appHome,
): string {
  const rawValue = Array.isArray(value) ? value[0] : value;

  if (typeof rawValue !== 'string' || rawValue.trim() === '') {
    return defaultPath;
  }

  const candidate = rawValue.trim();

  if (!candidate.startsWith('/') || candidate.startsWith('//')) {
    return defaultPath;
  }

  try {
    const url = new URL(candidate, 'https://directordeck.local');

    if (
      normalizePathname(url.pathname) ===
      normalizePathname(configuration.paths.signIn)
    ) {
      return defaultPath;
    }

    url.searchParams.delete(EXPLICIT_SIGN_OUT_QUERY_PARAM);
    url.searchParams.delete(SESSION_RECOVERY_QUERY_PARAM);

    const query = url.searchParams.toString();

    return `${url.pathname}${query ? `?${query}` : ''}`;
  } catch {
    return defaultPath;
  }
}

function replaceLocationOnce(destination: string): void {
  if (hasStartedRecoveryNavigation) {
    return;
  }

  hasStartedRecoveryNavigation = true;
  window.location.replace(destination);
}

function getCurrentPath(): string {
  return `${window.location.pathname}${window.location.search}`;
}

function normalizePathname(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

function hasApiErrorCode(payload: unknown, code: ApiErrorCode): boolean {
  return (
    Boolean(payload) &&
    typeof payload === 'object' &&
    (payload as { code?: unknown }).code === code
  );
}
